import {Component, Property, MeshComponent} from '@wonderlandengine/api';

/**
 * Video Wall Tiled - Complete solution for displaying ultra-wide videos
 * Splits video into tiles and maps them to wall segments
 * 
 * Material Mapping:
 * - material1: First third of video (0-2400px) → First wall segment
 * - material2: Second third of video (2400-4800px) → Second wall segment  
 * - material3: Third third of video (4800-7200px) → Third wall segment
 * 
 * For mesh with tiled walls:
 * - Left wall: Use materials 4, 5, 6 in mesh
 * - Right wall: Use materials 7, 8, 9 in mesh (rotated to avoid gap)
 */
export class VideoWallTiled extends Component {
    static TypeName = 'video-wall-tiled';
    static Properties = {
        videoUrl: Property.string('/static/video-7200.mp4'),
        // Original material properties for backward compatibility
        material1: Property.material(),  // First tile
        material2: Property.material(),  // Second tile
        material3: Property.material(),  // Third tile
        // Additional materials for right room (optional)
        rightMaterial1: Property.material(),
        rightMaterial2: Property.material(),
        rightMaterial3: Property.material(),
        tileWidth: Property.int(2400),
        videoWidth: Property.int(7200),
        videoHeight: Property.int(720),
        textureProperty: Property.string('diffuseTexture'),
        autoplay: Property.bool(true),
        muted: Property.bool(false),  // Whether to mute sound (false = audio enabled)
        loop: Property.bool(true),
        debugMode: Property.bool(true)
    };

    init() {
        this.video = null;
        this.tiles = [];
        this.updateCount = 0;
        this.materials = [];  // Will be populated based on room
        this.updateLoopStarted = false;  // Prevent multiple update loops
        this.currentRoom = 'left';  // Default room, will be set by playVideo()

        // Store material sets for quick access
        this.leftMaterials = [];
        this.rightMaterials = [];

        // Audio handling - bind context for event handlers
        this.playAfterUserGesture = this.playAfterUserGesture.bind(this);
    }

    start() {
        console.log('[VideoWallTiled] Starting tiled video wall system');

        // Use material1,2,3 as left room materials (backward compatibility)
        this.leftMaterials = [this.material1, this.material2, this.material3].filter(m => m !== null);
        // Right room materials are optional
        this.rightMaterials = [this.rightMaterial1, this.rightMaterial2, this.rightMaterial3].filter(m => m !== null);

        console.log('[VideoWallTiled] Material configuration:', {
            leftRoom: {
                material1: this.material1 ? 'assigned' : 'missing',
                material2: this.material2 ? 'assigned' : 'missing',
                material3: this.material3 ? 'assigned' : 'missing',
                total: this.leftMaterials.length
            },
            rightRoom: {
                material1: this.rightMaterial1 ? 'assigned' : 'missing',
                material2: this.rightMaterial2 ? 'assigned' : 'missing',
                material3: this.rightMaterial3 ? 'assigned' : 'missing',
                total: this.rightMaterials.length
            }
        });

        // Start with left room materials by default (using original material1,2,3)
        this.materials = this.leftMaterials;

        // Calculate number of tiles needed
        this.tileCount = Math.ceil(this.videoWidth / this.tileWidth);
        console.log(`[VideoWallTiled] Will create ${this.tileCount} tiles of ${this.tileWidth}px each`);

        // Validate that at least one room has materials
        if (this.leftMaterials.length === 0 && this.rightMaterials.length === 0) {
            console.error('[VideoWallTiled] No materials assigned for either room');
            console.log('[VideoWallTiled] Please assign leftMaterial1-3 or rightMaterial1-3');
            return;
        }

        // Initialize system
        this.createVideoSource();
        this.createTiles();
    }
    
    createVideoSource() {
        console.log('[VideoWallTiled] Creating video source');
        
        this.video = document.createElement('video');
        this.video.src = this.videoUrl;
        this.video.crossOrigin = 'anonymous';
        this.video.loop = this.loop;
        this.video.muted = this.muted;  // Now controlled by property
        this.video.playsInline = true;
        this.video.style.display = 'none';
        document.body.appendChild(this.video);
        
        this.video.addEventListener('loadedmetadata', () => {
            console.log(`[VideoWallTiled] Video loaded: ${this.video.videoWidth}x${this.video.videoHeight}`);
            
            // Verify dimensions
            if (this.video.videoWidth !== this.videoWidth || this.video.videoHeight !== this.videoHeight) {
                console.warn('[VideoWallTiled] Video dimensions differ from expected:');
                console.warn(`  Expected: ${this.videoWidth}x${this.videoHeight}`);
                console.warn(`  Actual: ${this.video.videoWidth}x${this.video.videoHeight}`);
                
                // Update to actual dimensions
                this.videoWidth = this.video.videoWidth;
                this.videoHeight = this.video.videoHeight;
                this.tileCount = Math.ceil(this.videoWidth / this.tileWidth);
            }
            
            this.onVideoReady();
        });
        
        this.video.addEventListener('error', (e) => {
            const error = this.video.error;
            console.error('[VideoWallTiled] Video error:', {
                code: error?.code,
                message: error?.message,
                mediaError: {
                    MEDIA_ERR_ABORTED: error?.code === 1 ? 'User aborted' : '',
                    MEDIA_ERR_NETWORK: error?.code === 2 ? 'Network error' : '',
                    MEDIA_ERR_DECODE: error?.code === 3 ? 'Decode error' : '',
                    MEDIA_ERR_SRC_NOT_SUPPORTED: error?.code === 4 ? 'Source not supported' : ''
                },
                url: this.videoUrl,
                readyState: this.video.readyState
            });
        });
        
        this.video.load();
    }
    
    createTiles() {
        console.log('[VideoWallTiled] Creating tile system');

        // For 'both' room mode, we display the same video on both walls
        const tilesPerWall = 3;
        const actualTileCount = this.currentRoom === 'both' ? 6 : 3;

        for (let i = 0; i < actualTileCount; i++) {
            // For 'both' mode, tiles 0-2 are left wall, 3-5 are right wall
            // Both sets show the same video content
            const tileIndexForSource = this.currentRoom === 'both' ? (i % tilesPerWall) : i;

            // Create canvas for this tile - always use full tile size
            const canvas = document.createElement('canvas');
            canvas.width = this.tileWidth; // Always 2400px for consistent tile size
            canvas.height = this.videoHeight;
            canvas.style.display = 'none';
            document.body.appendChild(canvas);

            const ctx = canvas.getContext('2d', {
                alpha: false,
                desynchronized: true
            });

            // Create tile object
            const tile = {
                index: i,
                canvas: canvas,
                ctx: ctx,
                texture: null,
                material: this.materials[i] || null,
                updateSuccessful: false
            };

            this.tiles.push(tile);

            const roomLabel = this.currentRoom === 'both'
                ? (i < 3 ? 'left' : 'right')
                : this.currentRoom;
            const tileLabel = `${roomLabel}_tile${tileIndexForSource}`;

            console.log(`[VideoWallTiled] Tile ${i} (${tileLabel}):`, {
                canvasSize: `${canvas.width}x${canvas.height}`,
                hasMaterial: !!tile.material,
                materialAssigned: tile.material ? 'Yes' : 'No - tile will not display'
            });
        }
    }
    
    onVideoReady() {
        console.log('[VideoWallTiled] Video ready, initializing textures');
        
        // Create and apply textures
        this.tiles.forEach((tile, index) => {
            if (!tile.material) {
                console.warn(`[VideoWallTiled] No material for tile ${index}, skipping`);
                return;
            }
            
            // Draw initial frame to canvas
            tile.ctx.fillStyle = '#000';
            tile.ctx.fillRect(0, 0, tile.canvas.width, tile.canvas.height);
            
            try {
                // Create texture from canvas
                console.log(`[VideoWallTiled] Creating texture for tile ${index}`);
                const texture = this.engine.textures.create(tile.canvas);
                
                if (texture) {
                    tile.texture = texture;
                    
                    // Apply to material - check all possible properties
                    const textureProps = [this.textureProperty, 'diffuseTexture', 'flatTexture', 'texture'];
                    let applied = false;

                    for (const prop of textureProps) {
                        try {
                            if (tile.material[prop] !== undefined) {
                                tile.material[prop] = texture;
                                console.log(`[VideoWallTiled] Texture applied to tile ${index} material using '${prop}'`);
                                applied = true;

                                // Log material info for debugging
                                console.log(`[VideoWallTiled] Material ${index} info:`, {
                                    hasDiffuseTexture: tile.material.diffuseTexture !== undefined,
                                    hasFlatTexture: tile.material.flatTexture !== undefined,
                                    hasTexture: tile.material.texture !== undefined,
                                    pipeline: tile.material.pipeline?.name || 'unknown'
                                });
                                break;
                            }
                        } catch (e) {
                            console.warn(`[VideoWallTiled] Failed to set ${prop} on material ${index}:`, e);
                        }
                    }

                    if (!applied) {
                        console.error(`[VideoWallTiled] Could not apply texture to material ${index} - no suitable property found`);
                        console.log(`[VideoWallTiled] Available material properties:`, Object.keys(tile.material));
                    }
                } else {
                    console.error(`[VideoWallTiled] Failed to create texture for tile ${index}`);
                }
            } catch (e) {
                console.error(`[VideoWallTiled] Error creating texture for tile ${index}:`, e);
            }
        });
        
        // Start playback
        this.startPlayback();
    }
    
    startPlayback() {
        console.log('[VideoWallTiled] Starting video playback');
        console.log(`[VideoWallTiled] Audio settings: muted=${this.muted}, autoplay=${this.autoplay}`);
        
        if (this.autoplay) {
            /* Muted videos are allowed to play immediately. Videos with sound
             * need to await a user gesture per browser autoplay policies */
            if (this.muted) {
                // Muted videos can play immediately
                this.video.play().then(() => {
                    console.log('[VideoWallTiled] Muted video playing automatically');
                    this.startUpdateLoop();
                }).catch(e => {
                    console.warn('[VideoWallTiled] Failed to autoplay muted video:', e);
                    // Fallback to user gesture even for muted videos
                    this.setupUserGestureHandlers();
                });
            } else {
                // Videos with audio need user interaction
                console.log('[VideoWallTiled] Video has audio - waiting for user interaction');
                this.setupUserGestureHandlers();
            }
        } else {
            console.log('[VideoWallTiled] Autoplay disabled - waiting for manual start');
            // Don't start automatically, wait for manual trigger
        }
    }
    
    setupUserGestureHandlers() {
        console.log('[VideoWallTiled] Setting up user gesture handlers for audio playback');
        window.addEventListener('click', this.playAfterUserGesture);
        window.addEventListener('touchstart', this.playAfterUserGesture);
    }
    
    playAfterUserGesture() {
        console.log('[VideoWallTiled] User gesture detected - starting video with audio');
        this.video?.play().then(() => {
            console.log('[VideoWallTiled] Video started after user gesture');
            this.startUpdateLoop();
        }).catch(e => {
            console.error('[VideoWallTiled] Failed to start video after user gesture:', e);
        });
        
        // Remove event listeners after successful start
        window.removeEventListener('click', this.playAfterUserGesture);
        window.removeEventListener('touchstart', this.playAfterUserGesture);
    }
    
    startUpdateLoop() {
        // Prevent multiple update loops
        if (this.updateLoopStarted) {
            console.log('[VideoWallTiled] Update loop already running');
            return;
        }
        this.updateLoopStarted = true;
        console.log('[VideoWallTiled] Starting update loop');
        
        let lastUpdate = 0;
        let lastDebugLog = Date.now();
        
        const update = (timestamp) => {
            // Throttle to 30fps
            if (timestamp - lastUpdate < 33) {
                requestAnimationFrame(update);
                return;
            }
            lastUpdate = timestamp;
            
            if (this.video && !this.video.paused && !this.video.ended) {
                // Calculate centering for narrow videos
                const wallWidth = 7200; // Expected wall width (3 tiles * 2400px)
                const videoIsNarrow = this.video.videoWidth < wallWidth;
                let offsetX = 0;

                if (videoIsNarrow) {
                    // Center the video on the wall
                    offsetX = (wallWidth - this.video.videoWidth) / 2;
                    if (this.updateCount === 0) {
                        console.log(`[VideoWallTiled] Centering narrow video: ${this.video.videoWidth}px centered in ${wallWidth}px (offset: ${offsetX}px)`);
                    }
                }

                // Update each tile
                this.tiles.forEach((tile) => {
                    if (!tile.texture) return;

                    // Clear canvas first
                    tile.ctx.fillStyle = '#000';
                    tile.ctx.fillRect(0, 0, tile.canvas.width, tile.canvas.height);

                    // For 'both' mode, tiles 3-5 should display the same content as tiles 0-2
                    const effectiveTileIndex = this.currentRoom === 'both' && tile.index >= 3
                        ? tile.index - 3
                        : tile.index;

                    // Calculate what part of the video to show on this tile
                    const tileStartX = effectiveTileIndex * this.tileWidth;
                    const tileEndX = tileStartX + this.tileWidth;

                    // Adjust for centering
                    const videoStartX = tileStartX - offsetX;
                    const videoEndX = tileEndX - offsetX;

                    // Only draw if this tile should show part of the video
                    if (videoEndX > 0 && videoStartX < this.video.videoWidth) {
                        const sourceX = Math.max(0, videoStartX);
                        const sourceEndX = Math.min(this.video.videoWidth, videoEndX);
                        const sourceWidth = sourceEndX - sourceX;
                        const destX = videoStartX < 0 ? Math.abs(videoStartX) : 0;

                        if (sourceWidth > 0) {
                            try {
                                // Draw video section to tile canvas
                                tile.ctx.drawImage(
                                    this.video,
                                    sourceX, 0, sourceWidth, this.video.videoHeight,  // Source from video
                                    destX, 0, sourceWidth, tile.canvas.height         // Destination on canvas
                                );

                                // Log first frame for debugging
                                if (this.updateCount === 0) {
                                    console.log(`[VideoWallTiled] Tile ${tile.index}: source(${sourceX}, ${sourceWidth}) -> dest(${destX}, ${sourceWidth})`);
                                }
                            } catch (e) {
                                if (this.updateCount === 0) {
                                    console.error(`[VideoWallTiled] Error drawing tile ${tile.index}:`, e);
                                }
                            }
                        }
                    }

                    // Update texture with canvas content
                    try {
                        if (tile.texture && tile.texture.update) {
                            tile.texture.update(tile.canvas);
                            tile.updateSuccessful = true;
                        } else if (tile.texture && tile.texture.upload) {
                            tile.texture.upload(tile.canvas);
                            tile.updateSuccessful = true;
                        } else {
                            // Try WebGL direct update as fallback
                            this.updateTextureDirectly(tile);
                            if (!tile.updateSuccessful && this.updateCount === 0) {
                                console.warn(`[VideoWallTiled] Tile ${tile.index} has no update method, trying fallback`);
                            }
                        }
                    } catch (e) {
                        if (!tile.updateSuccessful && this.updateCount === 0) {
                            console.error(`[VideoWallTiled] Update error for tile ${tile.index}:`, e);
                        }
                    }
                });
                
                this.updateCount++;
                
                // Debug logging
                if (this.debugMode) {
                    const now = Date.now();
                    if (now - lastDebugLog > 3000) {
                        const successfulTiles = this.tiles.filter(t => t.updateSuccessful).length;
                        console.log(`[VideoWallTiled] Updates: ${this.updateCount}, Time: ${this.video.currentTime.toFixed(1)}s, Active tiles: ${successfulTiles}/${this.tiles.length}`);
                        lastDebugLog = now;
                    }
                }
            }
            
            requestAnimationFrame(update);
        };
        
        requestAnimationFrame(update);
    }
    
    updateTextureDirectly(tile) {
        // Fallback: Try to update WebGL texture directly
        const canvas = document.querySelector('canvas');
        const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');

        if (gl && tile.texture) {
            try {
                // Bind and update texture
                gl.bindTexture(gl.TEXTURE_2D, tile.texture);
                gl.texSubImage2D(
                    gl.TEXTURE_2D, 0, 0, 0,
                    gl.RGBA, gl.UNSIGNED_BYTE, tile.canvas
                );
                tile.updateSuccessful = true;
            } catch (e) {
                // Silent fail after first attempt
            }
        }
    }

    /**
     * Play video in specific room(s)
     * @param {string} videoUrl - URL of the video to play
     * @param {string} room - Which room to play in: 'left', 'right', or 'both'
     */
    playVideo(videoUrl, room = 'left') {
        console.log(`[VideoWallTiled] Playing video in ${room} room(s): ${videoUrl}`);

        // Stop current video if playing
        if (this.video && !this.video.paused) {
            this.video.pause();
            this.updateLoopStarted = false;
        }

        // Set the room and update materials
        this.currentRoom = room;
        this.selectMaterialsForRoom(room);

        // Update video source if different
        if (videoUrl && this.videoUrl !== videoUrl) {
            this.videoUrl = videoUrl;
            if (this.video) {
                this.video.src = videoUrl;
                this.video.load();
            }
        }

        // Start playback
        if (this.video) {
            this.video.play().then(() => {
                console.log(`[VideoWallTiled] Video playing in ${room} room(s)`);
                if (!this.updateLoopStarted) {
                    this.startUpdateLoop();
                }
            }).catch(e => {
                console.error('[VideoWallTiled] Failed to play video:', e);
            });
        }
    }

    /**
     * Select materials based on room
     */
    selectMaterialsForRoom(room) {
        console.log(`[VideoWallTiled] Selecting materials for room: ${room}`);

        // Clear previous materials' textures to avoid conflicts
        if (this.tiles.length > 0) {
            this.tiles.forEach(tile => {
                if (tile.material && tile.material[this.textureProperty]) {
                    // Clear the texture from the old material
                    tile.material[this.textureProperty] = null;
                }
            });
        }

        switch(room) {
            case 'left':
                this.materials = this.leftMaterials;
                console.log(`[VideoWallTiled] Using LEFT materials:`, this.leftMaterials.length);
                break;
            case 'right':
                this.materials = this.rightMaterials;
                console.log(`[VideoWallTiled] Using RIGHT materials:`, this.rightMaterials.length);
                break;
            case 'both':
                // Combine both room materials for panoramic display
                this.materials = [...this.leftMaterials, ...this.rightMaterials];
                console.log(`[VideoWallTiled] Using BOTH materials:`, this.materials.length,
                    '(left:', this.leftMaterials.length, 'right:', this.rightMaterials.length, ')');
                break;
            default:
                console.warn(`[VideoWallTiled] Unknown room: ${room}, using left`);
                this.materials = this.leftMaterials;
        }

        // Update tiles with new materials
        if (this.tiles.length > 0) {
            this.updateTileMaterials();
        }

        console.log(`[VideoWallTiled] Active materials: ${this.materials.length}`);
    }

    /**
     * Update tile materials after room change
     */
    updateTileMaterials() {
        console.log(`[VideoWallTiled] Updating tile materials for room: ${this.currentRoom}`);

        // Always clear and recreate tiles when changing rooms
        console.log(`[VideoWallTiled] Clearing existing tiles and recreating for ${this.currentRoom} room(s)`);

        // Clear all existing tiles
        this.tiles.forEach(tile => {
            if (tile.canvas) {
                tile.canvas.remove();
            }
        });
        this.tiles = [];

        // Set tile count based on room
        if (this.currentRoom === 'both') {
            this.tileCount = 6; // 3 for left, 3 for right
        } else {
            this.tileCount = 3; // Just one wall
        }

        // Recreate tiles
        this.createTiles();

        // We need to wait for video to be ready if it exists
        if (this.video && this.video.readyState >= 2) {
            this.onVideoReady();
        }
    }

    /**
     * Stop video playback
     */
    stopVideo() {
        console.log('[VideoWallTiled] Stopping video');
        if (this.video) {
            this.video.pause();
            this.video.currentTime = 0;
            this.updateLoopStarted = false;

            // Clear all tile canvases to black
            this.tiles.forEach(tile => {
                if (tile.ctx) {
                    tile.ctx.fillStyle = '#000';
                    tile.ctx.fillRect(0, 0, tile.canvas.width, tile.canvas.height);
                    // Update texture with black canvas
                    if (tile.texture && tile.texture.update) {
                        tile.texture.update(tile.canvas);
                    }
                }
            });
        }
    }
    
    onDestroy() {
        // Clean up audio event listeners
        window.removeEventListener('click', this.playAfterUserGesture);
        window.removeEventListener('touchstart', this.playAfterUserGesture);
        
        // Clean up video element
        if (this.video) {
            this.video.pause();
            this.video.remove();
        }
        
        // Clean up tile canvases
        this.tiles.forEach(tile => {
            if (tile.canvas) {
                tile.canvas.remove();
            }
        });
    }
}